import {TaskType} from '../../shared/task';
import {useEffect, useState} from 'react';
import {createApiQuery} from './api';
import {GetTask, GetTaskResponse} from '../../shared/api/definitions/tasks';

export const TaskStatusQueryKeys = {
  all: ['taskStatus'],
  task: (taskType: TaskType | undefined) => [
    ...TaskStatusQueryKeys.all,
    taskType,
  ],
} as const;

const useTaskQuery = createApiQuery(
  GetTask,
  ({type}) => TaskStatusQueryKeys.task(type),
  {
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      // Refetch more often while the task is pending or running.
      if (state === 'pending' || state === 'running') {
        return 5_000;
      }

      return 60_000;
    },
  }
);

export function useTask(
  params: Parameters<typeof useTaskQuery>[0],
  {
    onTaskComplete,
    ...options
  }: Parameters<typeof useTaskQuery>[1] & {
    onTaskComplete?: (task: NonNullable<GetTaskResponse>) => void;
  } = {}
) {
  const [isTaskInProgress, setTaskInProgress] = useState(false);

  const taskQuery = useTaskQuery(params, options);
  const data = taskQuery.data;

  useEffect(() => {
    const state = data?.state;
    if (!state) {
      return;
    }

    setTaskInProgress((prevTaskInProgress) => {
      const taskInProgress = state === 'pending' || state === 'running';
      if (prevTaskInProgress && !taskInProgress) {
        onTaskComplete?.(data);
      }

      return taskInProgress;
    });
  }, [onTaskComplete, data]);

  return {
    taskQuery,
    isTaskInProgress,
  };
}
